import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

// ============================================
// INTERFACES
// ============================================

/** Raw company row from the companies table */
export interface Company {
  id: string;
  name: string;
  normalized_name?: string;
  parent_company_id?: string;
  primary_domain?: string;
  industry?: string;
  tiktok_category?: string;
  employee_count?: number;
  annual_revenue?: number;
  country?: string;
  city?: string;
  state?: string;
  founded_year?: number;
  ecommerce_platform?: string;
  estimated_sales?: number;
  estimated_visits?: number;
  total_gmv?: number;
  total_video_count?: number;
  total_creator_count?: number;
  enrichment_data?: Record<string, unknown>;
  enrichment_source?: string;
  enrichment_date?: string;
  logo_url?: string;
  description?: string;
  created_at: string;
  updated_at: string;
}

/** TAM membership data joined with a company */
export interface TAMMembership {
  id: string;
  company_id: string;
  client_id: string;
  priority_score?: number;
  tam_tier?: string;
  is_customer?: boolean;
  in_sales_cycle?: boolean;
  status?: string;
  notes?: string;
  tags?: unknown;
  added_by?: string;
  added_at: string;
  created_at: string;
  updated_at: string;
}

/** Company identifier (domain, linkedin, social, etc.) */
export interface CompanyIdentifier {
  id: string;
  company_id: string;
  identifier_type: string;
  identifier_value: string;
  raw_value?: string;
  is_primary?: boolean;
  source?: string;
}

/** Flattened company view combining company + TAM membership + identifiers.
 *  This is the primary interface used by UI components.
 *  Has an index signature so cf_* (custom field) values pass through generically. */
export interface CompanyView {
  // Custom-field passthrough — any `cf_*` key read by useCustomFields()
  [cfKey: `cf_${string}`]: any;
  // Company core
  id: string;
  name: string;
  primary_domain?: string;
  industry?: string;
  employee_count?: number;
  annual_revenue?: number;
  country?: string;
  city?: string;
  state?: string;
  founded_year?: number;
  ecommerce_platform?: string;
  estimated_sales?: number;
  estimated_visits?: number;
  total_gmv?: number;
  total_video_count?: number;
  total_creator_count?: number;
  enrichment_source?: string;
  enrichment_date?: string;
  logo_url?: string;
  description?: string;
  created_at: string;
  updated_at: string;
  // TAM membership (flattened)
  tam_membership_id?: string;
  tam_tier?: string;
  priority_score: number;
  /** TAM list membership tags jsonb — surfaces segment / sub_bucket /
   * Fastmoss GMV / influencer count etc. Shape varies by client/list;
   * consumers should read defensively. */
  tam_tags?: Record<string, any>;
  /** Provenance for the rolled-up `country` field: 'companies' |
   * 'aiark' | 'storeleads' | 'fastmoss_us_export' | … */
  country_source?: string;
  is_customer: boolean;
  in_sales_cycle: boolean;
  account_status: string;
  account_owner?: string;
  // Identifiers (flattened)
  domains: string[];
  linkedin_url?: string;
  identifiers: CompanyIdentifier[];
  // Backward-compatible aliases (for downstream components that use old Account shape)
  company_name: string;
  domain?: string;
  company_logo_url?: string;
}

/** Backward-compatible type alias so downstream components like AccountsTable
 *  can import { Account } from '@/hooks/useCompanies' without changes. */
export type Account = CompanyView;

// ============================================
// HOOK
// ============================================

export const useCompanies = (clientId?: number) => {
  const [companies, setCompanies] = useState<CompanyView[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCompanies = useCallback(async () => {
    try {
      setLoading(true);

      // Fetch companies with their TAM memberships
      let query = supabase
        .from('companies')
        .select(`
          *,
          tam_memberships(*),
          company_identifiers(*)
        `)
        .order('created_at', { ascending: false });

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;

      if (!data) {
        setCompanies([]);
        return;
      }

      // Filter by client_id if provided (via tam_memberships)
      const views: CompanyView[] = data
        .map((company: any) => {
          // Find the TAM membership for this client (if clientId specified)
          const memberships: TAMMembership[] = company.tam_memberships || [];
          const membership = clientId
            ? memberships.find((m: any) => String(m.client_id) === String(clientId))
            : memberships[0]; // If no clientId filter, take first membership

          // Extract identifiers
          const identifiers: CompanyIdentifier[] = company.company_identifiers || [];
          const domains = identifiers
            .filter((id: any) => id.identifier_type === 'domain')
            .map((id: any) => id.identifier_value);
          const linkedinId = identifiers.find((id: any) => id.identifier_type === 'linkedin');

          return {
            // Company core
            id: company.id,
            name: company.name,
            primary_domain: company.primary_domain,
            industry: company.industry,
            employee_count: company.employee_count,
            annual_revenue: company.annual_revenue,
            country: company.country,
            city: company.city,
            state: company.state,
            founded_year: company.founded_year,
            ecommerce_platform: company.ecommerce_platform,
            estimated_sales: company.estimated_sales,
            estimated_visits: company.estimated_visits,
            total_gmv: company.total_gmv,
            total_video_count: company.total_video_count,
            total_creator_count: company.total_creator_count,
            enrichment_source: company.enrichment_source,
            enrichment_date: company.enrichment_date,
            logo_url: company.logo_url,
            description: company.description,
            created_at: company.created_at,
            updated_at: company.updated_at,
            // TAM membership (flattened)
            tam_membership_id: membership?.id,
            tam_tier: membership?.tam_tier,
            priority_score: membership?.priority_score ?? 0,
            is_customer: membership?.is_customer ?? false,
            in_sales_cycle: membership?.in_sales_cycle ?? false,
            account_status: membership?.status || 'active',
            account_owner: membership?.added_by,
            // Identifiers
            domains,
            linkedin_url: linkedinId?.raw_value || linkedinId?.identifier_value,
            identifiers,
            // Backward-compatible aliases
            company_name: company.name,
            domain: company.primary_domain,
            company_logo_url: company.logo_url,
          } satisfies CompanyView;
        })
        // If clientId specified, only show companies that have a membership for that client
        .filter((view: CompanyView) => {
          if (!clientId) return true;
          return view.tam_membership_id != null;
        });

      setCompanies(views);
      setError(null);
    } catch (err) {
      console.error('Error fetching companies:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch companies');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  const deleteCompany = async (id: string) => {
    try {
      // Delete from companies (CASCADE will handle identifiers, storefronts, signals)
      const { error } = await supabase
        .from('companies')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Company deleted",
        description: "Company has been deleted successfully.",
      });

      await fetchCompanies();
    } catch (err) {
      console.error('Error deleting company:', err);
      toast({
        title: "Error",
        description: "Failed to delete company. Please try again.",
        variant: "destructive",
      });
      throw err;
    }
  };

  const bulkDeleteCompanies = async (ids: string[]) => {
    try {
      if (ids.length === 0) return;

      const { error } = await supabase
        .from('companies')
        .delete()
        .in('id', ids);

      if (error) throw error;

      toast({
        title: "Companies deleted",
        description: `Successfully deleted ${ids.length} compan${ids.length !== 1 ? 'ies' : 'y'}.`,
      });

      await fetchCompanies();
    } catch (err) {
      console.error('Error bulk deleting companies:', err);
      toast({
        title: "Error",
        description: "Failed to delete companies. Please try again.",
        variant: "destructive",
      });
      throw err;
    }
  };

  /** Upload accounts via CSV — routes through the ingest-accounts Edge Function
   *  which uses resolveCompany() for dedup */
  const uploadCSV = async (
    file: File,
    clientIdForUpload: number,
    tamListName?: string
  ) => {
    try {
      const text = await file.text();
      const lines = text.split('\n');
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase());

      const accounts: Record<string, unknown>[] = [];

      for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;

        // Handle CSV values with commas inside quotes
        const values = parseCSVLine(line);
        const account: Record<string, unknown> = {};

        headers.forEach((header, index) => {
          const value = values[index]?.trim();
          if (!value) return;

          switch (header) {
            case 'company_name':
            case 'company name':
            case 'name':
              account.name = value;
              break;
            case 'domain':
            case 'website':
            case 'website_url':
              account.domain = value.replace(/^https?:\/\//, '').replace(/\/$/, '');
              break;
            case 'linkedin_url':
            case 'linkedin':
              account.linkedin_url = value;
              break;
            case 'industry':
              account.industry = value;
              break;
            case 'employee_count':
            case 'employees':
              account.employee_count = value;
              break;
            case 'annual_revenue':
            case 'revenue':
              account.annual_revenue = parseFloat(value.replace(/[^\d.-]/g, '')) || undefined;
              break;
            case 'phone':
              // Not stored in companies — skip for now
              break;
            case 'city':
              account.city = value;
              break;
            case 'state':
              account.state = value;
              break;
            case 'country':
              account.country = value;
              break;
            case 'tam_tier':
            case 'tier':
              account.tam_tier = value;
              break;
            case 'priority_score':
            case 'priority':
              account.priority_score = parseInt(value) || undefined;
              break;
            case 'description':
              account.description = value;
              break;
            case 'account_status':
            case 'status':
              account.account_status = value;
              break;
            case 'account_owner':
            case 'owner':
              account.account_owner = value;
              break;
            case 'ecommerce_platform':
            case 'platform':
              account.ecommerce_platform = value;
              break;
            case 'is_customer':
              account.is_customer = value.toLowerCase() === 'true' || value === '1' || value.toLowerCase() === 'yes';
              break;
            case 'in_sales_cycle':
              account.in_sales_cycle = value.toLowerCase() === 'true' || value === '1' || value.toLowerCase() === 'yes';
              break;
            case 'founded_year':
              account.founded_year = parseInt(value) || undefined;
              break;
            case 'logo_url':
            case 'company_logo_url':
              account.logo_url = value;
              break;
          }
        });

        if (account.name) {
          accounts.push(account);
        }
      }

      if (accounts.length === 0) {
        throw new Error('No valid accounts found in CSV');
      }

      // Call the ingest-accounts Edge Function
      const { data, error: invokeError } = await supabase.functions.invoke('ingest-accounts', {
        body: {
          client_id: String(clientIdForUpload),
          tam_list_name: tamListName,
          accounts,
        },
      });

      if (invokeError) throw invokeError;

      const result = data as {
        total: number;
        created: number;
        matched: number;
        errors: number;
      };

      toast({
        title: "CSV uploaded successfully",
        description: `${result.total} accounts processed: ${result.created} created, ${result.matched} matched, ${result.errors} errors.`,
      });

      await fetchCompanies();
      return result;
    } catch (err) {
      console.error('Error uploading CSV:', err);
      toast({
        title: "Error",
        description: err instanceof Error ? err.message : "Failed to upload CSV. Please check the format.",
        variant: "destructive",
      });
      throw err;
    }
  };

  // ============================================
  // MAPPED UPLOAD (Column Mapping UI flow)
  // ============================================

  /** Strip empty strings, null, undefined — prevents overwriting good data with blanks */
  const cleanObject = (obj: Record<string, any>): Record<string, any> =>
    Object.fromEntries(
      Object.entries(obj).filter(([_, v]) => v !== null && v !== undefined && v !== '')
    );

  /** Normalize domain (clean https://, www., trailing slashes) */
  const normalizeDomainValue = (domain: string): string => {
    let d = domain.toLowerCase().trim();
    d = d.replace(/^https?:\/\//, '');
    d = d.replace(/^www\./, '');
    d = d.split('/')[0];
    d = d.split(':')[0];
    return d;
  };

  /** Normalize LinkedIn URL (clean protocol, www, query params, trailing slash) */
  const normalizeLinkedInValue = (url: string): string => {
    let u = url.toLowerCase().trim();
    u = u.replace(/^https?:\/\//, '');
    u = u.replace(/^www\./, '');
    u = u.split('?')[0];
    u = u.replace(/\/$/, '');
    return u;
  };

  /** Upload pre-mapped accounts from the Column Mapping UI.
   *  Accounts have already been transformed via transformDataWithMapping().
   *  Batches in chunks of 200 with a 5-minute timeout per batch to prevent
   *  browser-level fetch timeouts on large uploads. */
  const uploadMappedAccounts = async (
    accounts: Record<string, any>[],
    clientIdForUpload: number | string,
    tamListName?: string,
    onProgress?: (processed: number, total: number) => void,
  ): Promise<{ total: number; created: number; matched: number; errors: number }> => {
    try {
      if (accounts.length === 0) {
        throw new Error('No accounts to upload');
      }

      if (accounts.length > 50000) {
        throw new Error('Maximum 50,000 rows per upload. Please split your file into smaller batches.');
      }

      // Validate clientId exists
      const clientIdStr = String(clientIdForUpload)
      if (!clientIdForUpload || (typeof clientIdForUpload === 'number' && isNaN(clientIdForUpload)) || !clientIdStr) {
        throw new Error('A valid client must be selected before uploading accounts.');
      }

      // Prepare all account payloads upfront
      const preparedAccounts = accounts.map(a => {
        const rawDomain = a.domain || a.website_url;
        const payload: Record<string, any> = { ...a };
        payload.name = a.company_name;
        payload.company_name = a.company_name;
        payload.domain = rawDomain ? normalizeDomainValue(rawDomain) : undefined;
        payload.linkedin_url = a.linkedin_url ? normalizeLinkedInValue(a.linkedin_url) : undefined;
        if (typeof a.tags === 'string') {
          payload.tags = a.tags.split(',').map((t: string) => t.trim()).filter(Boolean);
        }
        return cleanObject(payload);
      });

      // Batch in chunks of 200 to prevent timeouts
      const BATCH_SIZE = 200;
      const totalBatches = Math.ceil(preparedAccounts.length / BATCH_SIZE);
      let totalCreated = 0, totalMatched = 0, totalErrors = 0;

      for (let batchIdx = 0; batchIdx < preparedAccounts.length; batchIdx += BATCH_SIZE) {
        const batch = preparedAccounts.slice(batchIdx, batchIdx + BATCH_SIZE);
        const currentBatch = Math.floor(batchIdx / BATCH_SIZE) + 1;

        onProgress?.(batchIdx, preparedAccounts.length);

        console.log(`[Upload] Batch ${currentBatch}/${totalBatches}: sending ${batch.length} accounts`);

        const { data: batchResult, error: batchError } = await supabase.functions.invoke('ingest-accounts-bulk', {
          body: {
            client_id: clientIdStr,
            tam_list_name: tamListName || undefined,
            accounts: batch,
          },
        });

        if (batchError) {
          console.error(`[Upload] Batch ${currentBatch}/${totalBatches} error:`, batchError.message);
          totalErrors += batch.length;
        } else if (batchResult) {
          totalCreated += batchResult.created || 0;
          totalMatched += batchResult.matched || 0;
          totalErrors += batchResult.errors || 0;
          console.log(`[Upload] Batch ${currentBatch}/${totalBatches}: ${batchResult.created} created, ${batchResult.matched} matched, ${batchResult.errors} errors`);
        }

        onProgress?.(batchIdx + batch.length, preparedAccounts.length);
      }

      const result = {
        total: preparedAccounts.length,
        created: totalCreated,
        matched: totalMatched,
        errors: totalErrors,
      };

      toast({
        title: "Upload successful",
        description: `${result.total} accounts processed: ${result.created} created, ${result.matched} matched${result.errors > 0 ? `, ${result.errors} errors` : ''}.`,
      });

      await fetchCompanies();
      return result;
    } catch (err) {
      console.error('Error uploading mapped accounts:', err);
      toast({
        title: "Upload failed",
        description: err instanceof Error ? err.message : "Failed to upload accounts.",
        variant: "destructive",
      });
      throw err;
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  return {
    companies,
    loading,
    error,
    deleteCompany,
    bulkDeleteCompanies,
    uploadCSV,
    uploadMappedAccounts,
    refetch: fetchCompanies,
  };
};

// ============================================
// CSV PARSING UTILITY
// ============================================

/** Parse a single CSV line handling quoted values with commas */
function parseCSVLine(line: string): string[] {
  const values: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i++; // Skip escaped quote
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      values.push(current.replace(/"/g, ''));
      current = '';
    } else {
      current += char;
    }
  }
  values.push(current.replace(/"/g, ''));
  return values;
}
